import React, { useState } from "react";
import { Box, Typography, IconButton, LinearProgress } from "@mui/material";
import { DeleteOutlined } from "@mui/icons-material";
import DescriptionIcon from "@mui/icons-material/Description";
import { useToast } from "../toast";
import { getStsToken } from "../../services/SdvFeature";
import { useQuery } from "react-query";
import { isFileTypeValid } from "../../services/Utils";
import LinearProgressWithLabel from "./LinearProgress";

const UploadListItemData = (props) => {
  const {
    uploadType,
    uploadText,
    hint,
    icon,
    iconBg,
    iconColor,
    fileType,
    assignFileUploadFunction,
    setMetaDataFile,
    isUploadInProgress,
  } = props;

  const { addToast } = useToast();
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileSelected, setFileSelected] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ loaded: 0, total: 100 });
  const [uploadStatus, setUploadStatus] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [uploadInstance, setUploadInstance] = useState(null);
  const [stsToken, setStsToken] = useState({
    accessKeyId: "",
    secretAccessKey: "",
    sessionToken: "",
  });

  useQuery("stsToken", getStsToken, {
    refetchOnWindowFocus: false,
    onSuccess: ({ data: token = {} }) => setStsToken(token),
    onError: ({ response: { data: errorData } }) => {
      if (errorData) addToast({ type: "error", message: errorData.message, autoClose: 3000 });
    },
  });

  const onMetaDataFileSelected = (item) => {
    const fileReader = new FileReader();
    const selected = item?.target?.files[0];
    fileReader.readAsText(selected);
    fileReader.onload = (e) => {
      try {
        setMetaDataFile(JSON.parse(e?.target?.result));
      } catch {
        addToast({
          type: "error",
          message: `"${selected?.name}" may not contain valid JSON!`,
          autoClose: 3000,
        });
        setMetaDataFile(null);
      }
    };
  };

  const validateFilePicked = (event) => {
    const selected = event?.target?.files[0];
    if (isFileTypeValid(selected?.name, fileType?.accept)) {
      setFileName(selected?.name);
      setSelectedFile(event.target.files[0]);
      setFileSelected(true);
      if (uploadType === "metadata") {
        onMetaDataFileSelected(event);
      } else {
        assignFileUploadFunction(
          uploadType,
          event.target.files[0],
          stsToken,
          setUploadInstance,
          setUploadProgress,
          setErrorMsg,
          errorMsg,
          setUploadStatus
        );
      }
    } else {
      setFileSelected(false);
      addToast({
        type: "error",
        message: `"${selected?.name}" is not a valid ${fileType?.name} file!`,
        autoClose: 3000,
      });
    }
  };

  const removeSelectedFile = () => {
    if (uploadType !== "metadata") assignFileUploadFunction(uploadType, null);
    setFileName("");
    setSelectedFile(null);
    setFileSelected(false);
    setMetaDataFile("");
  };

  if (fileSelected) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          border: "0.5px solid",
          borderColor: "divider",
          borderRadius: 2,
          bgcolor: "background.paper",
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1.5,
            bgcolor: iconBg || "action.hover",
            color: iconColor || "text.secondary",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "text.primary",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {fileName}
          </Typography>
          {uploadProgress?.loaded !== 0 && (
            <Box sx={{ mt: 0.5 }}>
              <LinearProgressWithLabel {...uploadProgress} />
            </Box>
          )}
        </Box>

        {!isUploadInProgress && (
          <IconButton
            size="small"
            onClick={removeSelectedFile}
            aria-label="Remove file"
            sx={{ color: "text.disabled", "&:hover": { color: "error.main" } }}
          >
            <DeleteOutlined fontSize="small" />
          </IconButton>
        )}
      </Box>
    );
  }

  return (
    <Box
      component="label"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        border: "0.5px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "background.paper",
        cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": {
          borderColor: "text.secondary",
          bgcolor: "action.hover",
        },
      }}
    >
      <input
        hidden
        type="file"
        accept={fileType?.accept}
        onChange={validateFilePicked}
      />

      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          bgcolor: iconBg || "action.hover",
          color: iconColor || "text.secondary",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary" }}>
          {uploadText}
        </Typography>
        <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
          {hint || "Click to choose a file"}
        </Typography>
      </Box>
    </Box>
  );
};

export default UploadListItemData;